import React from 'react';
import styled from 'styled-components';

import {
  BUTTON_ROLES,
  BUTTON_THEMES,
  BUTTON_TYPES,
} from '../../../shared/constants';

import { DEQ_TYPES } from '../../../utils/deq';
import {
  getElementAction,
  getIconItem,
  getLinkAttributes,
  getToLink,
  renderContent,
} from '../../../utils/generic';

interface Props {
  target?: string;
  textAlign?: 'left' | 'center' | 'right';
  type: 'internal' | 'external' | 'file' | 'submit';
  label: string;
  to?: string;
  className?: string;
  setOpenMenu?: (parameter: boolean) => void;
  iconPosition?: 'start' | 'end';
  leadingIcon?:
    | 'download'
    | 'calculator'
    | 'magnifying-glass'
    | 'rocket'
    | 'sheet'
    | 'document'; // TODO: Make a shared enum of this?
  icon: 'none' | 'mail' | 'default' | 'chevron' | 'arrow' | 'close';
  deqComponentTitle?: string;
  onButtonClick?: (...params: any[]) => void;
  showModal?: boolean;
  deqCustomLabel?: string;
  deqCustomType?: string;
  deqCustomAttributes?: { [key: string]: any };
  buttonTheme?: 'default' | 'highlight' | 'muted' | 'choiceButton';
  buttonAction?: 'submit' | 'reset' | 'button';
}

interface StyledComponentProps {
  themeType: string;
  textAlign?: string;
}

const SecondaryButton = ({
  target = '_self',
  textAlign = 'center',
  type,
  label,
  to,
  className,
  setOpenMenu,
  iconPosition = 'end',
  icon,
  leadingIcon,
  deqComponentTitle = undefined,
  onButtonClick,
  showModal = false,
  deqCustomType,
  deqCustomLabel,
  deqCustomAttributes = {},
  buttonTheme = BUTTON_THEMES.default,
  buttonAction,
}: Props) => {
  const trackEvent = () => {
    if (setOpenMenu) {
      return setOpenMenu(false);
    }
  };

  const isCustomClick = !!onButtonClick && !to;
  const attributes = getLinkAttributes(showModal, to, type, target);
  const link = to ? (getToLink(showModal, to, type) as any) : null;
  const trackingElementType =
    deqCustomAttributes?.elementType || DEQ_TYPES.textLink;
  const trackingButtonTarget = `${
    (to as unknown as { slug: string })?.slug ||
    to ||
    deqCustomAttributes?.elementTarget
  }`;

  const optionalTargetTracking = !isCustomClick
    ? {
        'data-element-target': trackingButtonTarget,
      }
    : {};

  return (
    <Button
      data-testid="secondary-button"
      href={link}
      textAlign={textAlign}
      onClick={(event) => {
        trackEvent();
        if (onButtonClick) {
          onButtonClick(to, type, event);
        }
      }}
      role={BUTTON_ROLES[type] || BUTTON_ROLES.internal}
      className={className}
      themeType={buttonTheme}
      data-element-label={deqCustomLabel?.toLowerCase() || label?.toLowerCase()}
      {...optionalTargetTracking}
      data-element-type={trackingElementType}
      data-element-style={
        trackingElementType !== DEQ_TYPES.textLink
          ? BUTTON_TYPES.secondary
          : undefined
      }
      data-element-action={buttonAction || getElementAction(type)}
      data-component-title={deqComponentTitle?.toLowerCase()}
      data-component-type={
        deqCustomType ||
        deqCustomAttributes?.componentType ||
        DEQ_TYPES.secondaryAnkeiler
      }
      {...attributes}
    >
      {leadingIcon && getIconItem(leadingIcon, 'start')}
      {renderContent(iconPosition, icon, type, label, BUTTON_TYPES.secondary)}
    </Button>
  );
};

export default SecondaryButton;

const Button = styled.a<StyledComponentProps>`
  align-items: center;
  color: ${({ theme, themeType }) => theme.secondaryButton[themeType].color};
  cursor: pointer;
  display: inline-flex;
  font-family: ${({ theme, themeType }) =>
    theme.secondaryButton[themeType].fontFamily};
  justify-content: ${({ theme, themeType }) =>
    theme.secondaryButton[themeType].textAlign};
  text-decoration: none;

  > svg {
    flex-shrink: 0;
    transition: transform 0.15s ease;
  }
  > svg.start {
    margin-right: 4px;
  }
  > svg.end {
    margin-left: 4px;
  }

  &:hover {
    color: ${({ theme, themeType }) =>
      theme.secondaryButton[themeType].hoverColor};
  }

  &:active,
  &:focus {
    color: ${({ theme, themeType }) =>
      theme.secondaryButton[themeType].activeColor};
  }

  &:hover,
  &:active,
  &:focus {
    text-decoration: underline;

    > svg.animate-icon {
      transform: translateX(4px);
    }

    > svg.animate-icon-left {
      transform: translateX(-4px);
    }

    > svg.animate-icon-down {
      transform: translateY(-2px);
    }
  }
`;
